import {
  ApiOutlined,
  GlobalOutlined,
  PaperClipOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import {
  Body,
  DropdownMenu,
  type DropdownMenuEntry,
  Icon,
  Button as IndigoButton,
} from "@indigo-components";
import { ChatAttachmentTile } from "@indigo-components/components/Chat";
import { Input } from "antd";
import { useAtom } from "jotai";
import {
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { SkillMenuItem } from "~/atoms/chatbot";
import { chatbotSlashPrefillAtom } from "~/atoms/chatbot";
import { getCurrentEnv } from "~/common/utils";
import { useDictationManager } from "~/hooks/useDictationManager";
import { useDictationWarmup } from "~/hooks/usePreconnectDictation";
import { filterSkills } from "~/pages/appointments/components/chatbotSkills/filterSkills";
import { findSlashTokenAtCursor } from "~/pages/appointments/components/chatbotSkills/parseSlashCommand";
import {
  SkillSlashMenu,
  SLASH_LISTBOX_ID,
  slashOptionId,
} from "~/pages/appointments/components/chatbotSkills/SkillSlashMenu";
import {
  ACCEPTED_FILE_TYPES,
  CHAT_PROVIDER_OPTIONS,
  type ChatAnnotation,
  REASONING_EFFORT_OPTIONS,
  type UseAgentChatReturn,
  warmupAgentChat,
} from "~/pages/appointments/hooks/useAgentChat";
import {
  failedSends,
  recallEntries,
  rememberFailedSend,
} from "~/pages/indy/composerHistory";
import { composerSuggestion } from "~/pages/indy/composerSuggestion";
import { INDY_COMPOSER_ATTRIBUTE } from "~/pages/indy/indyComposerTarget";
import { useQueuedMessages } from "~/pages/indy/useQueuedMessages";
import { useFeatureFlag } from "~/services/featureFlags";
import { FEATURE_FLAGS } from "~/services/featureFlags/constants";
import { PendingAnnotationsChip } from "./ChatAnnotations";
import {
  MAX_INPUT_CHARS,
  renderPendingAttachmentIcon,
} from "./chatSurfaceShared";
import { fieldHeight } from "./composerAutosize";
import { restoredScrollTop } from "./composerScroll";
import {
  INDY_DICTATION_SECTION_ID,
  IndyDictationButton,
} from "./IndyDictationButton";
import { pastedAttachments } from "./pastedAttachments";
import { QueuedMessages } from "./QueuedMessages";
import "./ChatComposer.css";

interface ChatComposerProps {
  layout: "panel" | "fullscreen";
  /** Fullscreen at phone widths — see `IndyChatSurfaceProps.mobile`. */
  mobile?: boolean;
  /** Gates one-shot effects (slash prefill, skill refresh). Panel: `open`. */
  active: boolean;
  chat: UseAgentChatReturn;
  availableSkills: SkillMenuItem[];
  /** `refreshIfStale` from useAvailableSkills — called when the surface activates. */
  refreshAvailableSkills: () => void;
  value: string;
  onValueChange: (value: string) => void;
}

export const ChatComposer: FC<ChatComposerProps> = ({
  layout,
  mobile = false,
  active,
  chat,
  availableSkills,
  refreshAvailableSkills,
  value,
  onValueChange,
}) => {
  const {
    conversationId,
    messages,
    sendMessage,
    stopStreaming,
    isStreaming,
    isAwaitingIdle,
    rejectedMessage,
    isUploading,
    pendingAttachments,
    addAttachment,
    removeAttachment,
    pendingAnnotations,
    updateAnnotation,
    removeAnnotation,
    clearAnnotations,
    restoreAnnotations,
    webSearchEnabled,
    setWebSearchEnabled,
    reasoningEffort,
    setReasoningEffort,
    provider,
    setProvider,
  } = chat;

  const fullscreen = layout === "fullscreen";
  // The drawer and phone widths get the controls on the field's own line and
  // no caption under it — both run tighter than the main canvas, and the
  // second row is the first thing worth spending there. Effort moves back
  // into the plus menu to compensate, so the state stays reachable.
  const compact = !fullscreen || mobile;

  // Restore each rejected send once, ahead of any draft; clearing the field
  // afterwards must remain possible.
  const restoredDraftRef = useRef<typeof rejectedMessage>(null);
  useEffect(() => {
    if (
      !rejectedMessage ||
      restoredDraftRef.current === rejectedMessage ||
      rejectedMessage.conversationId !== conversationId
    )
      return;
    restoredDraftRef.current = rejectedMessage;
    rememberFailedSend(
      conversationId,
      rejectedMessage.content,
      rejectedMessage.timestamp,
    );
    onValueChange(
      value ? `${rejectedMessage.content}\n${value}` : rejectedMessage.content,
    );
  }, [rejectedMessage, conversationId, value, onValueChange]);

  // Tracks the textarea's cursor position for slash detection.
  const [cursorPos, setCursorPos] = useState(0);
  const { isEnabled: dictationEnabled } = useFeatureFlag(
    FEATURE_FLAGS.INDY_DICTATION,
  );
  const { isEnabled: geminiEnabled } = useFeatureFlag(
    FEATURE_FLAGS.ENABLED_INDY_GEMINI,
  );
  const dictation = useDictationManager();
  // Warming here rather than only on the push-to-talk hold: a cold connect runs
  // 2.5-4s, far longer than the hold threshold, and a hold that outlives its
  // own connect drops the utterance. Focusing or typing in the composer
  // precedes reaching for the mic by seconds, and re-arms the connection the
  // service's 5-minute idle timeout closed. No-ops once warm.
  // Name the surface explicitly: IndyChatSurface claims the intent atom in a
  // parent effect, but this textarea carries autoFocus, so the first focus warm
  // can run before that lands. Warming on the atom would negotiate an
  // Assessment-shaped session that gets torn down a tick later.
  const warmDictation = useDictationWarmup("indy");
  const warmDictationIfEnabled = useCallback(() => {
    if (dictationEnabled) warmDictation();
  }, [dictationEnabled, warmDictation]);
  // The mic button stays a fixed-size icon, so the instruction to finish lives
  // here instead of widening the control with a label.
  const isDictating =
    dictation.isActive && dictation.activeSection === INDY_DICTATION_SECTION_ID;

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Last scroll offset the browser settled on, captured before each resize
  // pass resets it. Restored below once the pass is done.
  const savedScrollTopRef = useRef(0);
  // Whether the field has stopped growing and is genuinely overflowing — the
  // only state that shows a scrollbar.
  const [fieldOverflowing, setFieldOverflowing] = useState(false);

  const maxRows = mobile ? 4 : 6;

  // We size the field ourselves rather than handing Ant Design an `autoSize`,
  // which measures a shared hidden clone that cannot see our scrollbar gutter.
  // See `composerAutosize` for what that cost us.
  //
  // Releasing the height is what lets the field shrink: `scrollHeight` never
  // reports less than the box already is, so a field that has grown to six
  // lines would stay there after the text is deleted. Nothing paints between
  // the release and the write — both happen inside one layout effect — and the
  // gutter means letting go of the height cannot change the width either, so
  // the measurement is taken against the same line box the writer sees.
  //
  // Released to zero rather than `auto`: `auto` on a textarea falls back to
  // its `rows`, so the measurement floors at two lines and a field that has
  // grown can never come back to one.
  //
  // This depends on the field not transitioning `height` — a transitioned box
  // answers a height it is still animating away from, which reads as "the
  // field grows but never shrinks". `ChatComposer.css` turns Ant Design's
  // `transition: all` off for that reason.
  const resizeField = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const cs = getComputedStyle(el);
    const lineHeight = Number.parseFloat(cs.lineHeight);
    // `normal` — nothing to derive a row count from. Leave the field alone
    // rather than apply a height computed from NaN.
    if (!Number.isFinite(lineHeight)) return;

    const scrollTop = el.scrollTop;
    el.style.height = "0px";
    const { height, overflowing } = fieldHeight({
      contentHeight: el.scrollHeight,
      lineHeight,
      padding:
        Number.parseFloat(cs.paddingTop) + Number.parseFloat(cs.paddingBottom),
      border:
        Number.parseFloat(cs.borderTopWidth) +
        Number.parseFloat(cs.borderBottomWidth),
      minRows: 1,
      maxRows,
    });
    el.style.height = `${height}px`;
    // The field stopped being scrollable while the height was released, which
    // zeroes `scrollTop`. Put it back for the unfocused case; when the field
    // has focus `applyCaretScroll` runs next and has the better answer.
    el.scrollTop = scrollTop;
    setFieldOverflowing(overflowing);
  }, [maxRows]);

  const applyCaretScroll = useCallback(() => {
    const el = textareaRef.current;
    if (!el || document.activeElement !== el) return;
    el.scrollTop = restoredScrollTop({
      savedScrollTop: savedScrollTopRef.current,
      caretAtEnd: (el.selectionStart ?? 0) >= el.value.length,
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
    });
  }, []);

  // Width, not height: the panel can be dragged narrower, attachment tiles can
  // take a line, and the drawer and full screen are different widths. Any of
  // those rewraps the text and changes how tall it needs to be.
  //
  // Re-running the resize from an observer watching the element the resize
  // sizes sounds circular, but `resizeField` is a pure function of the content
  // and the width: called twice over with neither changed it writes the same
  // height, and writing the height a node already has notifies nobody.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      resizeField();
      applyCaretScroll();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [resizeField, applyCaretScroll]);

  // Before paint, so the field is never briefly the wrong height. Covers
  // typing, history recall, and the programmatic inserts dictation makes —
  // those write `element.value` and dispatch an `input` event, so they arrive
  // here as a value change like any keystroke.
  useLayoutEffect(() => {
    resizeField();
    applyCaretScroll();
  }, [value, resizeField, applyCaretScroll]);

  // Shell-style recall. -1 = composing something new; 0+ indexes back through
  // sent messages. `draftRef` holds whatever was being typed when recall
  // started, so walking back down returns it rather than losing it.
  const [historyIdx, setHistoryIdx] = useState(-1);
  const draftRef = useRef("");

  /** Current input text, readable after the awaited send resolves. */
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // Each thread keeps its own input: switching away saves the field and any
  // recall browse, switching back restores them, and a thread never typed in
  // starts empty. In memory only, like the rest of recall.
  //
  // Except leaving a thread with no messages yet: the placeholder id a fresh
  // open starts on (swapped for the resumed thread once it loads — a skill
  // prefill lands before that) or an unsent new chat. Neither can be switched
  // back to, so the input follows the switch instead of being parked there.
  const inputByConversationRef = useRef(
    new Map<string, { value: string; historyIdx: number; draft: string }>(),
  );
  const prevConversationIdRef = useRef(conversationId);
  // Whether the thread shown in the PREVIOUS commit had messages: the switch
  // effect below runs before the one that updates this, and a load swaps the
  // messages in the same commit as the id.
  const prevHadMessagesRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs on a thread switch only, reading the outgoing thread's state as it stood at the switch
  useEffect(() => {
    const prev = prevConversationIdRef.current;
    if (prev === conversationId) return;
    prevConversationIdRef.current = conversationId;
    if (!prevHadMessagesRef.current) {
      // The text comes along as a plain draft; a browse position would index
      // the other thread's history.
      setHistoryIdx(-1);
      draftRef.current = "";
      return;
    }
    const saved = inputByConversationRef.current;
    saved.set(prev, {
      value: valueRef.current,
      historyIdx,
      draft: draftRef.current,
    });
    const next = saved.get(conversationId);
    saved.delete(conversationId);
    onValueChange(next?.value ?? "");
    setHistoryIdx(next?.historyIdx ?? -1);
    draftRef.current = next?.draft ?? "";
  }, [conversationId]);
  useEffect(() => {
    prevHadMessagesRef.current = messages.length > 0;
  }, [messages]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Refresh the skill list when the surface activates (TTL-gated internally).
  useEffect(() => {
    if (active) void refreshAvailableSkills();
  }, [active, refreshAvailableSkills]);

  // One-shot slash prefill: the Skills editor's "Test invoke" button writes a
  // skill name to `chatbotSlashPrefillAtom` and opens the chat. Seed the input
  // with `/<name> ` and clear the atom so re-opens don't re-fire.
  const [slashPrefill, setSlashPrefill] = useAtom(chatbotSlashPrefillAtom);
  useEffect(() => {
    if (active && slashPrefill) {
      const next = `/${slashPrefill} `;
      onValueChange(next);
      setCursorPos(next.length);
      setSlashPrefill(null);
    }
  }, [active, slashPrefill, setSlashPrefill, onValueChange]);

  const slashToken = useMemo(
    () => findSlashTokenAtCursor(value, cursorPos),
    [value, cursorPos],
  );
  const matchingSkills = useMemo(
    () => (slashToken ? filterSkills(availableSkills, slashToken.query) : []),
    [slashToken, availableSkills],
  );
  const [slashActiveIdx, setSlashActiveIdx] = useState(0);
  // User dismissed the menu with Escape; resets on next input change.
  const [slashDismissed, setSlashDismissed] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    setSlashActiveIdx(0);
    setSlashDismissed(false);
  }, [value]);
  // Not while browsing recall: a recalled "/skill" message would open the
  // menu, which then takes the arrows and Enter. Typing ends the browse, so
  // editing the recalled text brings the menu back.
  const slashMenuVisible =
    historyIdx === -1 &&
    !slashDismissed &&
    slashToken != null &&
    matchingSkills.length > 0;

  /** Replace the current slash token with `/<picked>` + trailing space. */
  const completeSlashSelection = useCallback(
    (pickedName: string) => {
      if (!slashToken) return;
      const before = value.slice(0, slashToken.start);
      const after = value.slice(slashToken.end);
      const insert = `/${pickedName} `;
      const next = `${before}${insert}${after}`;
      onValueChange(next);
      const newCursor = slashToken.start + insert.length;
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(newCursor, newCursor);
          setCursorPos(newCursor);
        }
      });
    },
    [slashToken, value, onValueChange],
  );

  // The server runs one turn per conversation at a time; while one is in
  // flight nothing can be sent, and an upload is the same gate. So is a
  // finished reply whose session is not idle yet (closing or compacting):
  // text queues until then instead of sending as soon as the answer lands.
  // The status check on opening is not: `sendMessage` accepts the message and
  // holds only the request, so a first message never sits in the queue.
  const busy = isStreaming || isUploading || isAwaitingIdle;

  // Indy's guess at the next message, shown as the empty field's grey
  // placeholder. Tab takes it into the field as ordinary text, to edit or send.
  const suggestion = composerSuggestion(messages);
  const suggestionShown =
    suggestion !== null &&
    value === "" &&
    !busy &&
    !isDictating &&
    historyIdx === -1;
  const acceptSuggestion = useCallback(() => {
    if (!suggestion) return;
    onValueChange(suggestion);
    setCursorPos(suggestion.length);
    requestAnimationFrame(() =>
      textareaRef.current?.setSelectionRange(
        suggestion.length,
        suggestion.length,
      ),
    );
  }, [suggestion, onValueChange]);

  /**
   * Send *text* now. Failed sends (upload error, turn rejected) hand the text
   * back to the composer so nothing typed is lost — a queued message has no
   * other place to go, and a typed one is what was there anyway.
   */
  const sendText = useCallback(
    async (text: string, annotations: ChatAnnotation[]): Promise<boolean> => {
      const sent = await sendMessage(text, { annotations });
      if (!sent) {
        rememberFailedSend(conversationId, text, new Date());
        const current = valueRef.current;
        onValueChange(current ? `${text}\n${current}` : text);
        restoreAnnotations(annotations);
      }
      return sent;
    },
    [conversationId, sendMessage, onValueChange, restoreAnnotations],
  );

  // Owned here rather than in the stack: the drain has to know which row is
  // mid-edit so it does not send the pre-edit text out from under the editor.
  const [editingQueuedId, setEditingQueuedId] = useState<number | null>(null);
  const queue = useQueuedMessages({
    conversationId,
    busy,
    holdId: editingQueuedId,
    send: sendText,
  });
  const queuedTexts = queue.queued.map((m) => m.text);
  // For the label: numbered oldest = 1, so the newest reads N/N. Only needed
  // while browsing.
  const recallCount =
    historyIdx >= 0
      ? recallEntries(messages, failedSends(conversationId), queuedTexts).length
      : 0;

  /**
   * Move through history by *delta* (-1 = older, +1 = newer). Returns whether
   * it moved — the caller only swallows the keystroke when it did, so Up in a
   * multi-line message still moves the caret once history is exhausted.
   */
  const navigateHistory = useCallback(
    (delta: -1 | 1): boolean => {
      // Rebuilt per keystroke rather than memoized: failed sends live in a
      // module cache that does not re-render, and the merge is cheap.
      const entries = recallEntries(
        messages,
        failedSends(conversationId),
        queuedTexts,
      );
      const next = historyIdx - delta;
      if (next < -1 || next >= entries.length) {
        return false;
      }
      if (historyIdx === -1 && next >= 0) draftRef.current = valueRef.current;
      setHistoryIdx(next);
      const recalled = next === -1 ? draftRef.current : entries[next];
      onValueChange(recalled);
      // Land the caret at the end and move `cursorPos` with it. The slash
      // detector reads that state, so an offset left over from the previous
      // draft would open the skill menu mid-way through a recalled "/…"
      // message and splice the completion into the wrong span.
      setCursorPos(recalled.length);
      requestAnimationFrame(() =>
        textareaRef.current?.setSelectionRange(
          recalled.length,
          recalled.length,
        ),
      );
      return true;
    },
    [historyIdx, conversationId, messages, queuedTexts, onValueChange],
  );

  const handleSend = useCallback(
    async (e?: FormEvent) => {
      e?.preventDefault();
      const trimmed = value.trim();
      if (!trimmed && !pendingAttachments.length && !pendingAnnotations.length)
        return;

      // Busy with files or annotations only: nothing to queue (files ride
      // along with the next queued message; annotations wait for the next
      // message typed), so this keystroke
      // sends nothing and must leave the microphone alone too. Awaiting idle with an empty
      // queue, it sends: `sendMessage` holds the files until the session is.
      const filesHeld = isStreaming || isUploading || queue.queued.length > 0;
      if (busy && !trimmed && filesHeld) return;

      // Send is a commit, so it ends the dictation — below the guards above,
      // so a keystroke that sends nothing leaves the microphone alone.
      // Stopping first keeps the mic off the air during the upload. The
      // stop-flushed corrected final normally lands after the composer clears
      // and is dropped by the replace-only rule; a final that beats a slow
      // send can still rewrite the span and defeat the clear below — known
      // follow-up (PR #8787 review).
      if (isDictating) dictation.stopDictation();

      // Busy: queue the text and clear the field, instead of dropping the
      // keystroke. Staged annotations belong to this text, so they queue with
      // it; staged files stay in the composer and ride along with whichever
      // message sends next, as they always have.
      if (busy && trimmed) {
        queue.enqueue(trimmed, pendingAnnotations);
        clearAnnotations();
        setHistoryIdx(-1);
        draftRef.current = "";
        onValueChange("");
        return;
      }

      // When no text, use filenames as the message content for sidebar display.
      // Annotations alone send empty content: their text is not the user's,
      // so it must not become the message (or the conversation title).
      const isSynthetic = !trimmed && pendingAttachments.length > 0;
      const messageText =
        trimmed || pendingAttachments.map((a) => a.filename).join(", ");
      // Staged files upload inside sendMessage; hold the composer contents
      // until it confirms, so a failed upload doesn't lose what was typed.
      const submitted = value;
      const sent = await sendMessage(messageText, {
        isContentSynthetic: isSynthetic,
      });
      if (!sent) {
        // Never reached the transcript, so recall has no other record of it.
        rememberFailedSend(conversationId, messageText, new Date());
        return;
      }
      // The send ends this recall session. Clearing the field below is
      // programmatic, so the onChange reset never fires; without this, Up
      // resumes from the old index and skips the message just sent, and Down
      // restores a draft from before it.
      setHistoryIdx(-1);
      draftRef.current = "";
      // Clear only what was sent. The upload can take seconds, and anything
      // typed meanwhile is the start of the next message — keep it. If the
      // field was rewritten rather than appended to, leave it untouched.
      const current = valueRef.current;
      if (current === submitted) onValueChange("");
      else if (current.startsWith(submitted))
        onValueChange(current.slice(submitted.length));
    },
    [
      value,
      busy,
      queue.enqueue,
      sendMessage,
      pendingAttachments,
      pendingAnnotations,
      clearAnnotations,
      onValueChange,
      isDictating,
      dictation.stopDictation,
      conversationId,
      isStreaming,
      isUploading,
      queue.queued.length,
    ],
  );

  // Stop means nothing more goes out: queued messages come back to the field
  // (ahead of any draft) rather than firing the moment the reply stops. A
  // send stopped before it left is restored the same way, through
  // `rejectedMessage`, and lands first.
  const handleStop = useCallback(() => {
    stopStreaming();
    if (!queue.queued.length) return;
    const texts = queue.queued.map((m) => m.text);
    restoreAnnotations(queue.queued.flatMap((m) => m.annotations));
    queue.clear();
    onValueChange([...texts, valueRef.current].filter(Boolean).join("\n"));
  }, [
    stopStreaming,
    queue.queued,
    queue.clear,
    onValueChange,
    restoreAnnotations,
  ]);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;
      Array.from(files).forEach((file) => void addAttachment(file));
      e.target.value = "";
    },
    [addAttachment],
  );

  // Paste-to-attach: screenshots and copied files land on the clipboard as
  // File entries; ordinary text pastes fall through untouched.
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const files = pastedAttachments(e.clipboardData);
      if (!files.length) return;
      e.preventDefault();
      files.forEach((file) => void addAttachment(file));
    },
    [addAttachment],
  );

  const reasoningEffortLabel = useMemo(
    () =>
      REASONING_EFFORT_OPTIONS.find((opt) => opt.value === reasoningEffort)
        ?.label ?? "",
    [reasoningEffort],
  );

  // Provider comparison is a dev-only debug control.
  const providerSelectable = getCurrentEnv() === "dev" && geminiEnabled;
  const providerLabel =
    CHAT_PROVIDER_OPTIONS.find((opt) => opt.value === provider)?.label ?? "";

  // Wherever effort is offered, it is offered from here.
  const effortMenuItems: DropdownMenuEntry[] = useMemo(
    () =>
      REASONING_EFFORT_OPTIONS.map((opt) => ({
        label: opt.label,
        selected: opt.value === reasoningEffort,
        onClick: () => setReasoningEffort(opt.value),
      })),
    [reasoningEffort, setReasoningEffort],
  );

  // ── Plus menu (attach + web search, and effort where there is no caption) ──
  // Each row carries its own handler, so there is no key-string routing to keep
  // in sync with the item list. `selected` draws the design system's check.
  //
  // Effort appears in exactly one place at a time: the caption owns it on the
  // full-screen page, where the label is its own trigger, and the plus menu
  // owns it in the compact layouts, which have no caption. Offering it in both
  // at once would be two controls for one piece of state — the arrangement
  // this redesign set out to remove.
  const plusMenuItems: DropdownMenuEntry[] = useMemo(
    () => [
      {
        label: "Add photos & files",
        icon: <PaperClipOutlined />,
        onClick: () => fileInputRef.current?.click(),
      },
      { type: "divider" },
      {
        label: `Web search: ${webSearchEnabled ? "Auto" : "Off"}`,
        icon: <GlobalOutlined />,
        items: [
          {
            label: "Auto: internal sources first",
            selected: webSearchEnabled,
            selectionMode: "radio",
            onClick: () => setWebSearchEnabled(true),
          },
          {
            label: "Off",
            selected: !webSearchEnabled,
            selectionMode: "radio",
            onClick: () => setWebSearchEnabled(false),
          },
        ],
      },
      ...(compact
        ? [
            {
              label: "Reasoning effort",
              icon: <ThunderboltOutlined />,
              items: effortMenuItems,
            },
          ]
        : []),
      ...(providerSelectable
        ? [
            {
              label: `Provider: ${providerLabel}`,
              icon: <ApiOutlined />,
              items: CHAT_PROVIDER_OPTIONS.map((opt) => ({
                label: opt.label,
                selected: opt.value === provider,
                onClick: () => setProvider(opt.value),
              })),
            },
          ]
        : []),
    ],
    [
      compact,
      effortMenuItems,
      webSearchEnabled,
      setWebSearchEnabled,
      providerSelectable,
      providerLabel,
      provider,
      setProvider,
    ],
  );

  // The plus menu, the mic and the send/stop control, hoisted alongside
  // `fieldInput` so the compact row and the default two-row layout can each
  // place them without either copy drifting from the other.
  //
  // `top-right` is the design system's name for antd's old `topLeft`: opening
  // upward with the menu's left edge on the trigger's left. The hint goes
  // through `triggerTooltip` so it stays anchored to the button — wrapping the
  // component from outside also caught hovers on the menu rows, which are DOM
  // descendants of the same root.
  const plusMenu = (
    <DropdownMenu
      items={plusMenuItems}
      disabled={isStreaming || isUploading}
      dropDirection="top-right"
      onHoverOpen={false}
      submenuOnHover
      menuWidth="230px"
      triggerAriaLabel="Attach & options"
      triggerTooltip="Attach & options"
      buttonClassName="h-8 w-8 shrink-0 rounded-indigo-full p-0"
      trigger={
        isUploading ? (
          <Icon
            name="spinner"
            sizeX={16}
            sizeY={16}
            fill="var(--indigo-text-secondary)"
          />
        ) : (
          <Icon name="add" sizeX={16} fill="var(--indigo-text-secondary)" />
        )
      }
    />
  );

  // Shown while a reply streams too: the field takes text then (it queues),
  // so dictating into it is as valid as typing. It was hidden mid-stream
  // back when nothing typed could go anywhere.
  const micButton = dictationEnabled ? (
    <IndyDictationButton
      textareaRef={textareaRef}
      // Block starting a dictation mid-upload, never stopping one: while
      // listening this is the only control that ends a live microphone, and
      // an upload can outlast anyone's patience.
      disabled={isUploading && !isDictating}
    />
  ) : null;

  // Send and stop are the same circular slot at every width. The arrow says
  // "send" without a word of label, which is what let the textarea have the
  // width the "Send" button used to hold. `Button`, not `IconButton`: the
  // latter's `primary` is a tinted ghost, so a disabled send would lose its
  // circle and the target would vanish until you typed. While a reply runs
  // the slot is Stop; Enter is how text queues meanwhile.
  const actionButton = isStreaming ? (
    <IndigoButton
      variant="danger"
      size="md"
      type="button"
      onClick={handleStop}
      icon={<Icon name="stop" sizeX={13} />}
      aria-label="Stop"
      className="w-8 shrink-0 rounded-indigo-full p-0"
    />
  ) : (
    <IndigoButton
      variant="primary"
      size="md"
      type="button"
      disabled={
        isUploading ||
        (!value.trim() &&
          !pendingAttachments.length &&
          !pendingAnnotations.length)
      }
      onClick={() => void handleSend()}
      icon={<Icon name="arrow-up" sizeX={16} />}
      aria-label="Send"
      className="w-8 shrink-0 rounded-indigo-full p-0"
    />
  );

  const fieldInput = (
    <Input.TextArea
      ref={(el) => {
        // Ant Design's TextArea forwards the underlying <textarea> via
        // .resizableTextArea.textArea; we need that handle to adjust
        // selection after autocomplete.
        type AntInternal = {
          resizableTextArea?: { textArea?: HTMLTextAreaElement };
        };
        textareaRef.current =
          (el as unknown as AntInternal | null)?.resizableTextArea?.textArea ??
          null;
      }}
      // ARIA: present the textarea as a combobox while the slash menu is
      // open so screen readers announce the active option.
      role={slashMenuVisible ? "combobox" : undefined}
      aria-expanded={slashMenuVisible || undefined}
      aria-controls={slashMenuVisible ? SLASH_LISTBOX_ID : undefined}
      aria-activedescendant={
        slashMenuVisible
          ? slashOptionId(
              Math.max(0, Math.min(slashActiveIdx, matchingSkills.length - 1)),
            )
          : undefined
      }
      aria-autocomplete={slashMenuVisible ? "list" : undefined}
      value={value}
      onFocus={warmDictationIfEnabled}
      onChange={(e) => {
        // Grab the scroll offset before React re-renders: at this point the
        // browser has already scrolled this keystroke's caret into view, and
        // the autosize pass is about to throw that away. See `composerScroll`.
        savedScrollTopRef.current = e.target.scrollTop;
        // Typing means they are composing again, not browsing.
        if (historyIdx !== -1) setHistoryIdx(-1);
        onValueChange(e.target.value);
        setCursorPos(e.target.selectionStart ?? e.target.value.length);
        warmupAgentChat();
        warmDictationIfEnabled();
      }}
      onPaste={handlePaste}
      onSelect={(e) => {
        const t = e.currentTarget as HTMLTextAreaElement;
        setCursorPos(t.selectionStart ?? 0);
      }}
      onKeyUp={(e) => {
        const t = e.currentTarget as HTMLTextAreaElement;
        setCursorPos(t.selectionStart ?? 0);
      }}
      maxLength={MAX_INPUT_CHARS}
      placeholder={
        isDictating
          ? "Listening… click the mic to finish"
          : suggestionShown
            ? suggestion
            : "Ask anything..."
      }
      aria-description={
        suggestionShown ? "Press Tab to use the suggestion" : undefined
      }
      // One line until `resizeField` says otherwise. Only the height it has
      // before that first pass, but the default of 2 would open the composer
      // a line too tall.
      rows={1}
      // Off: we size the field ourselves in `resizeField`. Ant Design's
      // autosize measures a hidden clone that cannot see the scrollbar
      // gutter — see `composerAutosize`. With this falsy rc-textarea writes
      // no height, no maxHeight and no overflowY, so the node is ours.
      autoSize={false}
      // Never on a phone: autofocus throws up the on-screen keyboard over
      // half the screen before the user has read a word of the reply.
      autoFocus={!mobile}
      onKeyDown={(e) => {
        if (slashMenuVisible) {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setSlashActiveIdx((i) =>
              Math.min(i + 1, matchingSkills.length - 1),
            );
            return;
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setSlashActiveIdx((i) => Math.max(i - 1, 0));
            return;
          }
          if (e.key === "Escape") {
            e.preventDefault();
            // Stop the full-screen page's window-level Esc-to-exit from
            // firing — Escape here only dismisses the slash menu.
            e.stopPropagation();
            setSlashDismissed(true);
            return;
          }
          if (e.key === "Tab" || e.key === "Enter") {
            // Autocomplete the highlighted skill; never submit from the
            // menu. Shift+(Enter|Tab) passes through for newline / focus.
            if (e.shiftKey) return;
            if (e.nativeEvent.isComposing) return;
            e.preventDefault();
            const picked = matchingSkills[slashActiveIdx];
            if (picked) completeSlashSelection(picked.name);
            return;
          }
        }
        if (
          suggestionShown &&
          e.key === "Tab" &&
          !e.shiftKey &&
          !e.nativeEvent.isComposing
        ) {
          e.preventDefault();
          acceptSuggestion();
          return;
        }
        // Recall, once the slash menu has had its go at the arrows.
        // Gated on the caret's LINE, not the whole field: Up on the first
        // line recalls, Up anywhere below it moves the caret, so editing
        // a multi-line message still works. `navigateHistory` reports
        // whether it moved, so running out of history hands the keystroke
        // back rather than trapping the caret.
        if (
          (e.key === "ArrowUp" || e.key === "ArrowDown") &&
          !e.shiftKey &&
          !e.altKey &&
          !e.metaKey &&
          !e.ctrlKey &&
          !e.nativeEvent.isComposing
        ) {
          const t = e.currentTarget as HTMLTextAreaElement;
          const caret = t.selectionStart ?? 0;
          const onFirstLine = value.lastIndexOf("\n", caret - 1) === -1;
          const onLastLine = value.indexOf("\n", caret) === -1;
          if (
            (e.key === "ArrowUp" && onFirstLine && navigateHistory(-1)) ||
            (e.key === "ArrowDown" && onLastLine && navigateHistory(1))
          ) {
            e.preventDefault();
            return;
          }
        }
        if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault();
          void handleSend();
        }
      }}
      className="indy-composer__field"
      style={{
        // 16px on mobile: iOS Safari auto-zooms the page on focusing any
        // input under 16px, and never zooms back out.
        fontSize: mobile ? 16 : "var(--indigo-font-size-sm)",
      }}
    />
  );

  return (
    <div
      // Lets Assessment's double-tap-Control gesture stand down while this
      // composer holds focus — Control here belongs to push-to-talk. See
      // `indyComposerTarget`.
      {...{ [INDY_COMPOSER_ATTRIBUTE]: "" }}
      // No top rule: the message list is a sibling that clips at its own
      // bottom edge rather than scrolling under this bar, so the rule was only
      // ever separating the composer from the messages — which the surface's
      // own border and shadow now do.
      className="indy-composer px-4 py-3"
      style={{ flexShrink: 0 }}
    >
      <div className={fullscreen ? "mx-auto w-full max-w-[768px]" : undefined}>
        {value.length >= MAX_INPUT_CHARS && (
          <div className="flex justify-center" style={{ marginBottom: 4 }}>
            <div
              className="rounded-lg px-4 py-2 text-sm"
              style={{
                color: "var(--indigo-amber-800)",
                backgroundColor: "var(--indigo-amber-50)",
              }}
            >
              Character limit reached ({value.length.toLocaleString()} /{" "}
              {MAX_INPUT_CHARS.toLocaleString()})
            </div>
          </div>
        )}
        {/* Queued messages stack above the surface; the slash menu below is
            anchored after them so it opens over the stack, not under it. */}
        <QueuedMessages
          items={queue.queued}
          editingId={editingQueuedId}
          onEditingChange={setEditingQueuedId}
          onUpdate={queue.update}
          onRemove={queue.remove}
        />
        {/* Floating slash-autocomplete menu — positioned absolutely so
            appearing/disappearing doesn't reflow the input row. */}
        <div style={{ position: "relative" }}>
          {slashMenuVisible && (
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: "100%",
                marginBottom: 6,
                zIndex: 5,
              }}
            >
              <SkillSlashMenu
                skills={matchingSkills}
                activeIndex={slashActiveIdx}
                onHover={setSlashActiveIdx}
                onSelect={(s) => completeSlashSelection(s.name)}
              />
            </div>
          )}
        </div>
        <form
          onSubmit={(e) => void handleSend(e)}
          className={[
            "indy-composer__surface",
            compact && "indy-composer__surface--compact",
            fieldOverflowing && "indy-composer__surface--overflowing",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            multiple
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          {/* Staged attachments — thumbnails, uploaded only once sent. Inside
              the surface, above the field, so the composer reads as one
              object rather than tiles floating over a separate input. */}
          {(pendingAttachments.length > 0 || pendingAnnotations.length > 0) && (
            <div className="indy-composer__attachments flex flex-wrap items-center gap-2 px-1">
              {pendingAnnotations.length > 0 && (
                <PendingAnnotationsChip
                  annotations={pendingAnnotations}
                  onUpdate={updateAnnotation}
                  onRemove={removeAnnotation}
                  onClear={clearAnnotations}
                  editable={!isUploading}
                />
              )}
              {pendingAttachments.map((att, idx) => (
                <ChatAttachmentTile
                  key={att.uploadId}
                  previewUrl={att.previewUrl}
                  filename={att.filename}
                  icon={renderPendingAttachmentIcon(att)}
                  loading={isUploading}
                  onRemove={() => removeAttachment(idx)}
                />
              ))}
            </div>
          )}
          {/* Marks the field's text as a recalled message, not a draft, so
              Enter re-sends it knowingly. Its own line, like the tiles. */}
          {historyIdx >= 0 && recallCount > historyIdx && (
            <Body
              as="div"
              size="xs"
              className="indy-composer__recall text-indigo-text-tertiary"
            >
              History {recallCount - historyIdx}/{recallCount}
            </Body>
          )}
          {compact && plusMenu}
          {fieldInput}
          {compact ? (
            <>
              {micButton}
              {actionButton}
            </>
          ) : (
            <div className="indy-composer__controls">
              {plusMenu}
              <span className="indy-composer__controls-spacer" />
              {micButton}
              {actionButton}
            </div>
          )}
        </form>
        {/* Settings read-out, under the surface. Fullscreen only: the drawer
            and phone widths run tighter, and the line is not worth its height
            there — the plus menu and this menu both still reach the state. */}
        {/* The live region is the caption container, which is always mounted
            while the caption shows, not the web-search span inside it: a
            region that appears at the same moment as its own content
            announces nothing — assistive tech has to be watching it
            beforehand. */}
        {fullscreen && !mobile && (
          <div className="indy-composer__caption" aria-live="polite">
            <span
              className="indy-composer__caption-web"
              title="Auto checks internal sources first and searches the web only when needed."
            >
              <GlobalOutlined style={{ fontSize: 12 }} />
              Web search: {webSearchEnabled ? "Auto" : "Off"}
            </span>
            {/* A read-out like web search: the plus menu owns the control. */}
            {providerSelectable && (
              <span className="indy-composer__caption-web indy-composer__caption-provider">
                <ApiOutlined style={{ fontSize: 12 }} />
                {providerLabel}
              </span>
            )}
            <DropdownMenu
              items={effortMenuItems}
              dropDirection="top-left"
              onHoverOpen={false}
              menuWidth="160px"
              triggerAriaLabel={`Reasoning effort: ${reasoningEffortLabel}`}
              buttonClassName="h-6 px-2 rounded-indigo-sm"
              trigger={
                <Body as="span" size="xs" className="text-indigo-text-tertiary">
                  {reasoningEffortLabel}
                </Body>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};
